type AttributeValue = string | number | boolean;

/**
 * Flattens a nested object into a flat map of dot-notation keys to scalar
 * values, matching the shape Sentry metric/log attributes require (Sentry has
 * no nested-attribute support — see the metrics docs). `undefined`/`null` are
 * dropped; nested objects (and arrays, keyed by index) are recursed into;
 * non-scalar leaves (functions, symbols) are skipped.
 *
 *   flattenAttributes({ a: { b: 1 }, c: 'x' }) // → { 'a.b': 1, c: 'x' }
 */
export const flattenAttributes = (input: unknown, prefix = ''): Record<string, AttributeValue> => {
  if (input === null || input === undefined || typeof input !== 'object') {
    return {};
  }

  return Object.entries(input as Record<string, unknown>).reduce<Record<string, AttributeValue>>(
    (result, [key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key;

      if (value === null || value === undefined) {
        return result;
      }

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return { ...result, [path]: value };
      }

      if (typeof value === 'object') {
        return { ...result, ...flattenAttributes(value, path) };
      }

      return result;
    },
    {},
  );
};
